/**
 * Copyright © 2026 OwnersTable Inc. All rights reserved.
 * This source code is proprietary and confidential.
 * Unauthorized copying or distribution is strictly prohibited.
 */

import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Breadcrumbs,
  FormControl,
  IconButton,
  InputAdornment,
  Link,
  Select,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import ClearIcon from "@mui/icons-material/Clear";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import dayjs from "dayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { keepPreviousData, useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { NavigableBreadcrumbLink } from "../../../components/page_breadcrumbs/navigable_breadcrumb_link";
import api from "../../../api";
import authenticatedFetch from "../../../api/authenticatedFetch";
import { buildQueryParams } from "../../../utils/buildQueryParams";
import DropdownSearchBar from "../../../components/dorpdown_searchbar";
import { DropdownInfiniteLoader } from "../../../components/dropdown_infinite_loader";
import { IndexTable } from "../../../components/index_table";
import { Loader } from "../../../components/loader";
import { MenuHeading } from "../../../components/menu_heading";
import { ResetButton } from "../../../components/reset_button";
import { SelectedMenuItem } from "../../../components/selected_menu_item";
import { StyledMenuItem } from "../../../components/styled_menu_item";
import { UpdateButton } from "../../../components/update_button";
import { CheckInComplianceCards } from "../../../components/check_in_compliance_cards";
import { EligibleOrderModal } from "../../../components/eligible_order_modal";
import { ReconciledCheckInModal } from "../../../components/reconciled_check_in_modal";
import { Query_Keys } from "../../../lib/queryKeys";
import { useNotificationContext } from "../../../contexts/notificationContext";
import { PerPage } from "../../../constants/perPage";
import { formatWholeNumber } from "../../../utils/numberFormatter";
import { hasSingleItem } from "../../../utils/hasSingleItem";
import { getSingleItemValue } from "../../../utils/getSingleItemValue";
import { useDebounce } from "../../../utils/useDebounce";
import { isValidDayjsValue } from "../../../utils/isValidDayjsValue";
import {
  getDropdownNextPageParam,
  mergeOptionsIfChanged,
  mergeUniqueByKey,
  normalizeRestaurant,
  normalizeRestaurantGroup,
} from "../../../utils/dropdownInfiniteQuery";
import { useInfiniteScrollObserver } from "../../../hooks/useInfiniteScrollObserver";
import { ALL_FILTER_VALUE, formatPercent } from "../../../utils/perqReportDrilldown";
import {
  DATE_FILTERS,
  DEFAULT_SORT_BY,
  DEFAULT_SORT_ORDER,
  ALL_ORDER_TYPE,
  ORDER_TYPES,
  ORDER_TYPE_LABELS,
  ORDER_TYPE_PLACEHOLDER,
  defaultCheckInComplianceFilters,
  getCheckInComplianceApiFilters,
  getCheckInComplianceFilterError,
} from "./constants";
import {
  StyledBreadCrumbContainer,
  StyledLink,
  filterContainerStyles,
  selectBaseStyles,
  viewTypographyStyles,
  datePickerStyles,
  dropdownMenuProps,
  plainMenuProps,
} from "../perq_report/styles";

const normalizeDayjsValue = (value) => {
  if (!value) return null;
  if (typeof value?.isValid === "function" && typeof value?.format === "function") {
    return value;
  }
  const parsedValue = dayjs(value?.$d || value);
  return parsedValue.isValid() ? parsedValue : null;
};

const COLUMNS = [
  { key: "restaurant_name", label: "Restaurant", width: 220 },
  { key: "total_orders", label: "Total Orders", width: 130 },
  { key: "eligible_orders", label: "Eligible Orders", width: 140 },
  { key: "total_check_ins", label: "Total Check-Ins", width: 140 },
  { key: "reconciled_check_ins", label: "Reconciled Check-Ins", width: 170 },
  { key: "check_in_gap", label: "Check-In Gap", width: 130 },
  { key: "compliance_percent", label: "Approx. Compliance (%)", width: 170 },
];

export const CheckInCompliance = () => {
  const showNotification = useNotificationContext();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const [filters, setFilters] = useState(defaultCheckInComplianceFilters());
  const [appliedFilters, setAppliedFilters] = useState(defaultCheckInComplianceFilters());
  const [filtersChanged, setFiltersChanged] = useState(false);

  const [restaurantGroupSearch, setRestaurantGroupSearch] = useState("");
  const [restaurantSearch, setRestaurantSearch] = useState("");
  const [appliedPerqSearch, setAppliedPerqSearch] = useState("");
  const [restaurantGroupOptionsCache, setRestaurantGroupOptionsCache] = useState([]);
  const [restaurantOptionsCache, setRestaurantOptionsCache] = useState([]);

  const [itemsPerPage, setItemsPerPage] = useState(25);
  const [page, setPage] = useState(1);
  const [sortBy, setSortBy] = useState(DEFAULT_SORT_BY);
  const [sortOrder, setSortOrder] = useState(DEFAULT_SORT_ORDER);
  const [csvLoading, setCsvLoading] = useState(false);
  const [eligibleOrdersRestaurant, setEligibleOrdersRestaurant] = useState(null);
  const [reconciledCheckInsRestaurant, setReconciledCheckInsRestaurant] = useState(null);

  const debouncedRestaurantGroupSearch = useDebounce(restaurantGroupSearch.trim(), 300);
  const debouncedRestaurantSearch = useDebounce(restaurantSearch.trim(), 300);
  const debouncedAppliedPerqSearch = useDebounce(appliedPerqSearch.trim(), 300);

  const hasGroupSelected = filters.restaurant_group_id !== ALL_FILTER_VALUE;
  const hasRestaurantsSelected = !filters.restaurant_ids.includes(ALL_FILTER_VALUE);
  const hasAppliedPerqsSelected = filters.applied_discount_names.length > 0;
  const hasOrderTypeSelected = filters.order_type !== ALL_ORDER_TYPE;

  const apiFilters = useMemo(() => getCheckInComplianceApiFilters(appliedFilters), [appliedFilters]);
  // The report endpoints reject requests without a group or a valid date range.
  const canFetchReport = Boolean(apiFilters.restaurant_group_id && apiFilters.date_range);
  // Drill-ins are scoped to the clicked row's restaurant by the URL, so the restaurant filter is left out.
  const drillInFilters = useMemo(() => {
    const { restaurant_ids: _restaurantIds, ...rest } = apiFilters;
    return rest;
  }, [apiFilters]);

  const fetchRestaurantGroups = async ({ pageParam = 1 }) => {
    const response = await api.get("/v1/admin/dropdowns/restaurant_groups_list", {
      params: {
        page: pageParam,
        per_page: PerPage,
        search: debouncedRestaurantGroupSearch || undefined,
      },
    });
    return response?.data?.map(normalizeRestaurantGroup) || [];
  };

  const {
    data: restaurantGroups = [],
    isFetching: restaurantGroupFetching,
    isFetchingNextPage: restaurantGroupsFetchingNextPage,
    fetchNextPage: fetchNextRestaurantGroupsPage,
    hasNextPage: hasNextRestaurantGroupsPage,
  } = useInfiniteQuery({
    queryKey: Query_Keys.restaurant_groups_dropdown({
      search: debouncedRestaurantGroupSearch,
      source: "check_in_compliance",
    }),
    queryFn: fetchRestaurantGroups,
    initialPageParam: 1,
    getNextPageParam: getDropdownNextPageParam,
    select: (data) => data.pages.flat(),
    staleTime: 10 * 60 * 1000,
    placeholderData: keepPreviousData,
  });

  const { rootRef: restaurantGroupMenuRef, targetRef: restaurantGroupLoaderRef } =
    useInfiniteScrollObserver({
      fetchNextPage: fetchNextRestaurantGroupsPage,
      hasNextPage: hasNextRestaurantGroupsPage,
      isFetchingNextPage: restaurantGroupsFetchingNextPage,
    });

  const fetchRestaurants = async ({ pageParam = 1 }) => {
    const response = await api.get("/v1/admin/dropdowns/restaurants_list", {
      params: {
        page: pageParam,
        per_page: PerPage,
        search: debouncedRestaurantSearch || undefined,
        restaurant_group_ids: hasGroupSelected ? filters.restaurant_group_id : undefined,
        // The report only has rows for parent-level restaurants; a child's activity is in its parent's row.
        parent_level_only: true,
      },
    });
    return (
      response?.data?.map((record) => ({
        ...normalizeRestaurant(record),
        id: Number(record?.value ?? record?.id),
      })) || []
    );
  };

  const {
    data: restaurants = [],
    isFetching: restaurantFetching,
    isFetchingNextPage: restaurantsFetchingNextPage,
    fetchNextPage: fetchNextRestaurantsPage,
    hasNextPage: hasNextRestaurantsPage,
  } = useInfiniteQuery({
    queryKey: Query_Keys.restaurants_dropdown({
      restaurant_group_id: filters.restaurant_group_id,
      search: debouncedRestaurantSearch,
      source: "check_in_compliance",
    }),
    queryFn: fetchRestaurants,
    initialPageParam: 1,
    getNextPageParam: getDropdownNextPageParam,
    select: (data) => data.pages.flat(),
    staleTime: 10 * 60 * 1000,
    placeholderData: keepPreviousData,
  });

  const { rootRef: restaurantMenuRef, targetRef: restaurantLoaderRef } =
    useInfiniteScrollObserver({
      fetchNextPage: fetchNextRestaurantsPage,
      hasNextPage: hasNextRestaurantsPage,
      isFetchingNextPage: restaurantsFetchingNextPage,
    });

  const fetchAppliedPerqs = async ({ pageParam = 1 }) => {
    const response = await api.get("/v1/admin/dropdowns/managed_configured_discount_list", {
      params: {
        page: pageParam,
        per_page: PerPage,
        search: debouncedAppliedPerqSearch || undefined,
        restaurant_group_ids: hasGroupSelected ? filters.restaurant_group_id : undefined,
        restaurant_ids: hasRestaurantsSelected ? filters.restaurant_ids : undefined,
      },
    });
    return response?.data || [];
  };

  const {
    data: appliedPerqs = [],
    isFetching: appliedPerqsFetching,
    isFetchingNextPage: appliedPerqsFetchingNextPage,
    fetchNextPage: fetchNextAppliedPerqsPage,
    hasNextPage: hasNextAppliedPerqsPage,
  } = useInfiniteQuery({
    queryKey: Query_Keys.managed_configured_discounts_dropdown({
      restaurant_group_id: filters.restaurant_group_id,
      restaurant_ids: filters.restaurant_ids,
      search: debouncedAppliedPerqSearch,
    }),
    queryFn: fetchAppliedPerqs,
    initialPageParam: 1,
    getNextPageParam: getDropdownNextPageParam,
    select: (data) => data.pages.flat(),
    staleTime: 10 * 60 * 1000,
    placeholderData: keepPreviousData,
  });

  const { rootRef: appliedPerqMenuRef, targetRef: appliedPerqLoaderRef } =
    useInfiniteScrollObserver({
      fetchNextPage: fetchNextAppliedPerqsPage,
      hasNextPage: hasNextAppliedPerqsPage,
      isFetchingNextPage: appliedPerqsFetchingNextPage,
    });

  const fetchReport = async () => {
    const response = await api.get("/v1/admin/check_in_compliance", {
      params: {
        page,
        per_page: itemsPerPage,
        sort_by: sortBy,
        sort_order: sortOrder,
        ...apiFilters,
      },
    });

    return {
      data: response?.data || [],
      pageCount: Number(response?.headers?.["total-count"] || 0),
    };
  };

  const { data: report, isFetching, error } = useQuery({
    queryKey: Query_Keys.check_in_compliance({
      queryFilter: apiFilters,
      page,
      itemsPerPage,
      sortBy,
      sortOrder,
    }),
    queryFn: fetchReport,
    enabled: canFetchReport,
    staleTime: 10 * 60 * 1000,
    placeholderData: keepPreviousData,
  });

  const fetchSummary = async () => {
    const response = await api.get("/v1/admin/check_in_compliance/summary", {
      params: apiFilters,
    });
    return response?.data || {};
  };

  const {
    data: summaryData,
    isFetching: summaryFetching,
    error: summaryError,
  } = useQuery({
    queryKey: Query_Keys.check_in_compliance_summary({ queryFilter: apiFilters }),
    queryFn: fetchSummary,
    enabled: canFetchReport,
    staleTime: 10 * 60 * 1000,
    placeholderData: keepPreviousData,
  });

  useEffect(() => {
    setRestaurantGroupOptionsCache((current) =>
      mergeOptionsIfChanged(current, restaurantGroups, "label", ["value"]),
    );
  }, [restaurantGroups]);

  useEffect(() => {
    setRestaurantOptionsCache((current) =>
      mergeOptionsIfChanged(current, restaurants, "id", ["name"]),
    );
  }, [restaurants]);

  // A user with a single group gets it preselected and applied, since the report needs one.
  useEffect(() => {
    if (debouncedRestaurantGroupSearch) return;
    if (hasNextRestaurantGroupsPage) return;
    if (filters.restaurant_group_id !== ALL_FILTER_VALUE) return;
    if (!hasSingleItem(restaurantGroups)) return;

    const singleGroupId = getSingleItemValue(restaurantGroups)?.label;
    setFilters((prev) => ({ ...prev, restaurant_group_id: singleGroupId }));
    setAppliedFilters((prev) => ({ ...prev, restaurant_group_id: singleGroupId }));
  }, [debouncedRestaurantGroupSearch, hasNextRestaurantGroupsPage, restaurantGroups]);

  useEffect(() => {
    if (debouncedRestaurantSearch) return;
    if (hasNextRestaurantsPage) return;
    if (hasRestaurantsSelected) return;
    if (!hasSingleItem(restaurants)) return;

    const singleRestaurantId = getSingleItemValue(restaurants)?.id;
    setFilters((prev) => ({ ...prev, restaurant_ids: [singleRestaurantId] }));
  }, [debouncedRestaurantSearch, hasNextRestaurantsPage, restaurants]);

  useEffect(() => {
    const failed = error || summaryError;
    if (!failed) return;
    const message = failed?.response?.data?.error || "Failed to load check-in compliance report.";
    showNotification(message, "error");
  }, [error, summaryError, showNotification]);

  const selectedRestaurantGroupOption = restaurantGroupOptionsCache?.find(
    (group) => `${group?.label}` === `${filters.restaurant_group_id}`,
  );
  const displayedRestaurantGroups = restaurantGroupSearch.trim()
    ? restaurantGroups
    : selectedRestaurantGroupOption
      ? mergeUniqueByKey(restaurantGroups, [selectedRestaurantGroupOption], "label")
      : restaurantGroups;

  const selectedRestaurantOptions = restaurantOptionsCache?.filter((restaurant) =>
    filters.restaurant_ids?.some((id) => `${id}` === `${restaurant?.id}`),
  );
  const displayedRestaurants = restaurantSearch.trim()
    ? restaurants
    : mergeUniqueByKey(restaurants, selectedRestaurantOptions, "id");

  // Without a search term, keep selected perqs listed even when the current scope no longer returns them.
  const displayedAppliedPerqs = appliedPerqSearch.trim()
    ? appliedPerqs
    : mergeUniqueByKey(
        appliedPerqs,
        filters.applied_discount_names.map((name) => ({ label: name, value: name })),
        "value",
      );

  const getRestaurantGroupName = (id) => {
    if (id === ALL_FILTER_VALUE) return "Restaurant groups";
    const cached = restaurantGroupOptionsCache?.find((group) => `${group?.label}` === `${id}`);
    return cached?.value || "Restaurant groups";
  };

  const getRestaurantDisplayValue = () => {
    if (!hasRestaurantsSelected) return "Restaurants";
    if (filters.restaurant_ids.length === 1) {
      const restaurant = restaurantOptionsCache?.find(
        (record) => `${record.id}` === `${filters.restaurant_ids[0]}`,
      );
      return restaurant?.name || "Restaurants";
    }
    return `${filters.restaurant_ids.length} Restaurants Selected`;
  };

  const getAppliedPerqsDisplayValue = () => {
    if (!hasAppliedPerqsSelected) return "Managed applied perqs";
    if (filters.applied_discount_names.length === 1) return filters.applied_discount_names[0];
    return `${filters.applied_discount_names.length} Perqs Selected`;
  };

  const markChanged = () => setFiltersChanged(true);

  const handleDateFilterChange = (event) => {
    const nextDateFilter = event.target.value;
    setFilters((prev) => ({
      ...prev,
      dateFilter: nextDateFilter,
      customDateFrom: nextDateFilter === "Custom Date" ? null : prev.customDateFrom,
      customDateTo: nextDateFilter === "Custom Date" ? null : prev.customDateTo,
    }));
    markChanged();
  };

  const handleDateChange = (field, value) => {
    setFilters((prev) => {
      if (field === "customDateFrom") {
        if (!value) return { ...prev, customDateFrom: null, customDateTo: null };

        return {
          ...prev,
          customDateFrom: value,
          customDateTo:
            isValidDayjsValue(value) && !isValidDayjsValue(prev.customDateTo)
              ? dayjs().startOf("day")
              : prev.customDateTo,
        };
      }

      return { ...prev, [field]: normalizeDayjsValue(value) };
    });
    markChanged();
  };

  const resetGroupScopedFilters = (restaurantGroupId) => {
    setRestaurantSearch("");
    setAppliedPerqSearch("");
    setFilters((prev) => ({
      ...prev,
      restaurant_group_id: restaurantGroupId,
      restaurant_ids: [ALL_FILTER_VALUE],
      applied_discount_names: [],
    }));
    markChanged();
  };

  const handleRestaurantGroupChange = (event) => resetGroupScopedFilters(event.target.value);
  const handleRestaurantGroupClear = () => resetGroupScopedFilters(ALL_FILTER_VALUE);

  const handleRestaurantChange = (event) => {
    const { value } = event.target;
    if ((value.length === 1 && value.includes(ALL_FILTER_VALUE)) || value.length === 0) {
      setFilters((prev) => ({ ...prev, restaurant_ids: [ALL_FILTER_VALUE] }));
    } else {
      setFilters((prev) => ({
        ...prev,
        restaurant_ids: value.filter((record) => record !== ALL_FILTER_VALUE),
      }));
    }
    markChanged();
  };

  const handleRestaurantClear = () => {
    setFilters((prev) => ({ ...prev, restaurant_ids: [ALL_FILTER_VALUE] }));
    markChanged();
  };

  const handleOrderTypeChange = (event) => {
    setFilters((prev) => ({ ...prev, order_type: event.target.value }));
    markChanged();
  };

  const handleOrderTypeClear = () => {
    setFilters((prev) => ({ ...prev, order_type: ALL_ORDER_TYPE }));
    markChanged();
  };

  const handleAppliedPerqsChange = (event) => {
    const { value } = event.target;
    setFilters((prev) => ({
      ...prev,
      applied_discount_names: (Array.isArray(value) ? value : []).filter(Boolean),
    }));
    markChanged();
  };

  const handleAppliedPerqsClear = () => {
    setFilters((prev) => ({ ...prev, applied_discount_names: [] }));
    markChanged();
  };

  const handleColumnSortChange = (nextSortBy, nextSortOrder) => {
    setSortBy(nextSortBy);
    setSortOrder(nextSortOrder);
    setPage(1);
  };

  const handleApplyFilters = () => {
    const filterError = getCheckInComplianceFilterError(filters);
    if (filterError) {
      showNotification(filterError, "error");
      return;
    }

    setAppliedFilters(filters);
    setFiltersChanged(false);
    setPage(1);
  };

  const handleReset = () => {
    const resetFilters = defaultCheckInComplianceFilters();
    // Keep a preselected single group, since the report can't run without one.
    if (hasSingleItem(restaurantGroups) && !hasNextRestaurantGroupsPage) {
      resetFilters.restaurant_group_id = getSingleItemValue(restaurantGroups)?.label;
    }
    setFilters(resetFilters);
    setAppliedFilters(resetFilters);
    setFiltersChanged(false);
    setPage(1);
    setItemsPerPage(25);
    setSortBy(DEFAULT_SORT_BY);
    setSortOrder(DEFAULT_SORT_ORDER);
    setRestaurantGroupSearch("");
    setRestaurantSearch("");
    setAppliedPerqSearch("");
  };

  const handleExportCsv = async () => {
    try {
      setCsvLoading(true);

      const query = buildQueryParams({
        report: "check_in_compliance_report",
        sort_by: sortBy,
        sort_order: sortOrder,
        ...apiFilters,
      });

      const { response } = await authenticatedFetch(
        `${process.env.REACT_APP_API_BASE_URL}/v1/admin/csv_reports/export?${query}`,
        {
          method: "GET",
          defaultErrorMessage: "Failed to download CSV",
        },
      );

      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");

      link.href = downloadUrl;
      link.download = `check_in_compliance_report_${dayjs().format("YYYY-MM-DD")}.csv`;
      link.click();

      window.URL.revokeObjectURL(downloadUrl);
      showNotification("CSV downloaded successfully", "success");
    } catch (exportError) {
      showNotification(exportError?.message || "Failed to download CSV", "error");
    } finally {
      setCsvLoading(false);
    }
  };

  // A zero count stays plain text; anything above opens the drill-in for that row's restaurant.
  const renderDrillInCount = (count, restaurant, onOpen, testId) => {
    const formatted = formatWholeNumber(count);
    if (!(Number(count) > 0) || !restaurant?.id) return formatted;

    return (
      <Link
        component="button"
        type="button"
        underline="hover"
        data-testid={`${testId}-${restaurant.id}`}
        onClick={() => onOpen(restaurant)}
      >
        {formatted}
      </Link>
    );
  };

  const rows = useMemo(
    () =>
      (report?.data || []).map((record) => ({
        restaurant_name: record?.restaurant?.name || "-",
        total_orders: formatWholeNumber(record?.total_orders),
        eligible_orders: renderDrillInCount(
          record?.eligible_orders,
          record?.restaurant,
          setEligibleOrdersRestaurant,
          "check-in-compliance-eligible-orders",
        ),
        total_check_ins: formatWholeNumber(record?.total_check_ins),
        reconciled_check_ins: renderDrillInCount(
          record?.reconciled_check_ins,
          record?.restaurant,
          setReconciledCheckInsRestaurant,
          "check-in-compliance-reconciled-check-ins",
        ),
        check_in_gap: formatWholeNumber(record?.check_in_gap),
        compliance_percent: formatPercent(record?.compliance_percent),
      })),
    [report?.data],
  );

  return (
    <Box sx={{ height: "100%", width: "100%", overflowY: "scroll", overflowX: "hidden" }}>
      <StyledBreadCrumbContainer>
        <Breadcrumbs>
          <NavigableBreadcrumbLink LinkComponent={StyledLink} label="Home" to="/admin/dashboard" />
          <StyledLink color="#515152">Reports</StyledLink>
          <StyledLink color="#515152">Perq Reports</StyledLink>
          <StyledLink color="#515152">Check-In Compliance</StyledLink>
        </Breadcrumbs>
      </StyledBreadCrumbContainer>

      <Box sx={filterContainerStyles}>
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", lg: "row" },
            justifyContent: "space-between",
            alignItems: { xs: "flex-start", lg: "center" },
            gap: { xs: 2, lg: 1 },
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              gap: { xs: 2, sm: "12px" },
              flexWrap: "wrap",
              alignItems: { xs: "flex-start", sm: "center" },
              flex: 1,
            }}
          >
            <Typography variant="body2" sx={viewTypographyStyles}>
              View
            </Typography>

            <FormControl size="small" sx={{ minWidth: { xs: "100%", sm: "150px" } }}>
              <Select
                data-testid="check-in-compliance-date-filter"
                value={filters.dateFilter}
                renderValue={() => filters.dateFilter}
                onChange={handleDateFilterChange}
                sx={selectBaseStyles}
                MenuProps={plainMenuProps}
              >
                {DATE_FILTERS.map((value) => (
                  <StyledMenuItem key={value} value={value}>
                    {filters.dateFilter === value ? <SelectedMenuItem data={value} /> : value}
                  </StyledMenuItem>
                ))}
              </Select>
            </FormControl>

            {filters.dateFilter === "Custom Date" ? (
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, alignItems: "center" }}>
                <Typography variant="body2" sx={viewTypographyStyles}>
                  from
                </Typography>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DatePicker
                    format="MM-DD-YYYY"
                    value={normalizeDayjsValue(filters.customDateFrom)}
                    onChange={(value) => handleDateChange("customDateFrom", value)}
                    slots={{ openPickerIcon: CalendarMonthIcon }}
                    slotProps={{
                      textField: {
                        size: "small",
                        sx: { ...datePickerStyles, minWidth: 140 },
                        inputProps: { "data-testid": "check-in-compliance-date-from" },
                      },
                    }}
                  />
                </LocalizationProvider>
                <Typography variant="body2" sx={viewTypographyStyles}>
                  through
                </Typography>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DatePicker
                    format="MM-DD-YYYY"
                    value={normalizeDayjsValue(filters.customDateTo)}
                    onChange={(value) => handleDateChange("customDateTo", value)}
                    slots={{ openPickerIcon: CalendarMonthIcon }}
                    slotProps={{
                      textField: {
                        size: "small",
                        sx: { ...datePickerStyles, minWidth: 140 },
                        inputProps: { "data-testid": "check-in-compliance-date-to" },
                      },
                    }}
                  />
                </LocalizationProvider>
              </Box>
            ) : null}

            <FormControl size="small" sx={{ minWidth: { xs: "100%", sm: "185px" } }}>
              <Select
                data-testid="check-in-compliance-restaurant-group"
                value={hasGroupSelected ? filters.restaurant_group_id : ""}
                displayEmpty
                renderValue={() => getRestaurantGroupName(filters.restaurant_group_id)}
                onChange={handleRestaurantGroupChange}
                onClose={() => setTimeout(() => setRestaurantGroupSearch(""), 250)}
                sx={selectBaseStyles}
                endAdornment={
                  hasGroupSelected ? (
                    <InputAdornment position="end" sx={{ position: "absolute", right: 25 }}>
                      <IconButton edge="end" size="small" disableRipple onClick={handleRestaurantGroupClear}>
                        <ClearIcon sx={{ fontSize: "20px" }} />
                      </IconButton>
                    </InputAdornment>
                  ) : null
                }
                MenuProps={dropdownMenuProps(restaurantGroupMenuRef)}
              >
                <DropdownSearchBar
                  changeSearchText={setRestaurantGroupSearch}
                  placeholder="Search restaurant group"
                  isFetching={restaurantGroupFetching}
                />
                {displayedRestaurantGroups.map((group) => (
                  <StyledMenuItem key={group.label} value={group.label}>
                    {`${filters.restaurant_group_id}` === `${group.label}` ? (
                      <SelectedMenuItem data={group.value} />
                    ) : (
                      group.value
                    )}
                  </StyledMenuItem>
                ))}
                <DropdownInfiniteLoader
                  hasNextPage={hasNextRestaurantGroupsPage}
                  targetRef={restaurantGroupLoaderRef}
                />
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: { xs: "100%", sm: "195px" } }}>
              <Select
                data-testid="check-in-compliance-restaurant"
                multiple
                value={hasRestaurantsSelected ? filters.restaurant_ids : []}
                displayEmpty
                renderValue={getRestaurantDisplayValue}
                onChange={handleRestaurantChange}
                onClose={() => setTimeout(() => setRestaurantSearch(""), 250)}
                sx={selectBaseStyles}
                endAdornment={
                  hasRestaurantsSelected ? (
                    <InputAdornment position="end" sx={{ position: "absolute", right: 25 }}>
                      <IconButton edge="end" size="small" disableRipple onClick={handleRestaurantClear}>
                        <ClearIcon sx={{ fontSize: "20px" }} />
                      </IconButton>
                    </InputAdornment>
                  ) : null
                }
                MenuProps={dropdownMenuProps(restaurantMenuRef)}
              >
                <DropdownSearchBar
                  changeSearchText={setRestaurantSearch}
                  placeholder="Search restaurant"
                  isFetching={restaurantFetching}
                />
                {displayedRestaurants.map((restaurant) => (
                  <StyledMenuItem key={restaurant.id} value={restaurant.id}>
                    {filters.restaurant_ids.includes(restaurant.id) ? (
                      <SelectedMenuItem data={restaurant.name} />
                    ) : (
                      restaurant.name
                    )}
                  </StyledMenuItem>
                ))}
                <DropdownInfiniteLoader
                  hasNextPage={hasNextRestaurantsPage}
                  targetRef={restaurantLoaderRef}
                />
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: { xs: "100%", sm: "150px" } }}>
              <Select
                data-testid="check-in-compliance-order-type"
                value={hasOrderTypeSelected ? filters.order_type : ""}
                displayEmpty
                renderValue={() =>
                  hasOrderTypeSelected ? ORDER_TYPE_LABELS[filters.order_type] : ORDER_TYPE_PLACEHOLDER
                }
                onChange={handleOrderTypeChange}
                sx={selectBaseStyles}
                endAdornment={
                  hasOrderTypeSelected ? (
                    <InputAdornment position="end" sx={{ position: "absolute", right: 25 }}>
                      <IconButton edge="end" size="small" disableRipple onClick={handleOrderTypeClear}>
                        <ClearIcon sx={{ fontSize: "20px" }} />
                      </IconButton>
                    </InputAdornment>
                  ) : null
                }
                MenuProps={plainMenuProps}
              >
                {ORDER_TYPES.map((value) => (
                  <StyledMenuItem key={value} value={value}>
                    {filters.order_type === value ? (
                      <SelectedMenuItem data={ORDER_TYPE_LABELS[value]} />
                    ) : (
                      ORDER_TYPE_LABELS[value]
                    )}
                  </StyledMenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: { xs: "100%", sm: "185px" } }}>
              <Select
                data-testid="check-in-compliance-applied-perqs"
                multiple
                displayEmpty
                value={filters.applied_discount_names}
                renderValue={getAppliedPerqsDisplayValue}
                onChange={handleAppliedPerqsChange}
                onClose={() => setTimeout(() => setAppliedPerqSearch(""), 250)}
                sx={selectBaseStyles}
                endAdornment={
                  hasAppliedPerqsSelected ? (
                    <InputAdornment position="end" sx={{ position: "absolute", right: 25 }}>
                      <IconButton edge="end" size="small" disableRipple onClick={handleAppliedPerqsClear}>
                        <ClearIcon sx={{ fontSize: "20px" }} />
                      </IconButton>
                    </InputAdornment>
                  ) : null
                }
                MenuProps={dropdownMenuProps(appliedPerqMenuRef)}
              >
                <DropdownSearchBar
                  changeSearchText={setAppliedPerqSearch}
                  placeholder="Search managed applied perq"
                  isFetching={appliedPerqsFetching}
                />
                {displayedAppliedPerqs.map((perq) => (
                  <StyledMenuItem key={perq.value} value={perq.value}>
                    {filters.applied_discount_names.includes(perq.value) ? (
                      <SelectedMenuItem data={perq.label} />
                    ) : (
                      perq.label
                    )}
                  </StyledMenuItem>
                ))}
                <DropdownInfiniteLoader
                  hasNextPage={hasNextAppliedPerqsPage}
                  targetRef={appliedPerqLoaderRef}
                />
              </Select>
            </FormControl>
          </Box>

          <Box
            sx={{
              display: "flex",
              gap: 1,
              alignItems: "center",
              flexWrap: "wrap",
              width: { xs: "100%", lg: "auto" },
            }}
          >
            <ResetButton onClick={handleReset} />
            <UpdateButton
              onClick={handleApplyFilters}
              filtersChanged={filtersChanged}
              status={isFetching}
            />
          </Box>
        </Box>
      </Box>

      <MenuHeading report={false}>Check-In Compliance Report</MenuHeading>

      {canFetchReport ? (
        <CheckInComplianceCards
          isMobile={isMobile}
          data={summaryData}
          loading={summaryFetching}
        />
      ) : null}

      <Box sx={{ pb: 2 }}>
        {!canFetchReport ? (
          <Alert data-testid="check-in-compliance-alert-message" severity="info">
            Select a restaurant group
          </Alert>
        ) : isFetching ? (
          <Box
            data-testid="check-in-compliance-table-loader"
            role="status"
            aria-live="polite"
            aria-label="Loading report results"
          >
            <Loader />
          </Box>
        ) : (
          <IndexTable
            report={false}
            columns={COLUMNS}
            data={rows}
            type="restaurants"
            tableId="check-in-compliance-report"
            itemsPerPage={itemsPerPage}
            setItemsPerPage={setItemsPerPage}
            page={page}
            setPage={setPage}
            pageCount={report?.pageCount || 0}
            sortMode="server"
            sortBy={sortBy}
            sortOrder={sortOrder}
            onSortChange={handleColumnSortChange}
            showDownloadButton={true}
            disableDownloadButton={rows.length === 0 || csvLoading}
            handleExportCsv={handleExportCsv}
            csvLoading={csvLoading}
          />
        )}
      </Box>

      {/* Mounted per open so each drill-in starts on page 1 with the default sort. */}
      {eligibleOrdersRestaurant ? (
        <EligibleOrderModal
          open
          handleClose={() => setEligibleOrdersRestaurant(null)}
          restaurant={eligibleOrdersRestaurant}
          apiFilters={drillInFilters}
        />
      ) : null}

      {reconciledCheckInsRestaurant ? (
        <ReconciledCheckInModal
          open
          handleClose={() => setReconciledCheckInsRestaurant(null)}
          restaurant={reconciledCheckInsRestaurant}
          apiFilters={drillInFilters}
        />
      ) : null}
    </Box>
  );
};
